import React, { useEffect } from 'react';
import styled from 'styled-components';
import Seo from '../components/common/Seo';
import { colors, breakpoints } from '../styles/variables';

const Page = styled.div`
  background: ${colors.white};
`;

const Hero = styled.section`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 140%);
  color: ${colors.white};
  padding: 120px 24px 56px;
`;

const HeroInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 38px;
  font-weight: 700;
  margin: 0 0 14px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 28px;
  }
`;

const Lead = styled.p`
  font-size: 17px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.85);
  max-width: 760px;
  margin: 0;
`;

const Section = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px;
`;

const AvatarNote = styled.aside`
  margin: 0 0 32px;
  padding: 14px 18px;
  background: ${colors.lightGray};
  border-left: 3px solid ${colors.secondary};
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.7;
  color: ${colors.tertiary};

  strong {
    color: ${colors.primary};
  }
`;

const InstructorRow = styled.article`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 40px;
  align-items: start;
  padding: 40px 0;
  border-bottom: 1px solid ${colors.lightGray};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const PhotoWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  max-width: 280px;
  margin: 0 auto;
`;

const Photo = styled.img`
  width: 100%;
  max-width: 280px;
  border-radius: 12px;
  display: block;
`;

const PhotoCaption = styled.span`
  display: block;
  width: 100%;
  text-align: center;
  font-size: 12px;
  color: ${colors.tertiary};
  opacity: 0.75;
`;

const Info = styled.div``;

const Name = styled.h2`
  font-size: 26px;
  font-weight: 700;
  color: ${colors.primary};
  margin: 0 0 6px;
`;

const Role = styled.div`
  font-size: 16px;
  color: ${colors.secondary};
  font-weight: 600;
  margin-bottom: 16px;
`;

const Bio = styled.p`
  font-size: 15px;
  line-height: 1.85;
  color: ${colors.textGray};
  margin: 0 0 18px;
`;

const Specialties = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
`;

const Chip = styled.span`
  background: ${colors.lightGray};
  color: ${colors.tertiary};
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: 500;
`;

const Courses = styled.div`
  font-size: 14px;
  color: ${colors.tertiary};

  strong {
    color: ${colors.primary};
    margin-right: 6px;
  }
`;

const InstructorsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Page>
      <Seo
        title="강사진 소개"
        description="ElectroWave Club과 함께하는 멘토를 소개합니다. 현업 경험이 풍부한 전문가가 직접 코드 리뷰와 줌 1:1 멘토링을 진행합니다."
        path="/instructors"
        ogType="website"
      />

      <Hero>
        <HeroInner>
          <Title>함께하는 멘토</Title>
          <Lead>
            현업에서 코드를 쓰고 있는 개발자가 직접 멘토로 참여합니다. 영상 강의가 아니라 실시간 코드 리뷰,
            페어 프로그래밍, Q&A로 가르칩니다.
          </Lead>
        </HeroInner>
      </Hero>

      <Section>
        <AvatarNote>
          <strong>아바타 사용 안내</strong> — ElectroWave Club은 Zoom 미팅에서 <strong>얼굴 노출을 기본으로
          요구하지 않습니다.</strong> 멘토·학생 모두 AI 생성 아바타로 참여해 사생활 부담 없이 코드와 실습에
          집중하며, 강사진 소개 사진도 같은 정책에 따라 AI 아바타로 표시됩니다. 원하는 경우에 한해 양쪽 합의로
          실제 카메라 사용도 가능합니다.
        </AvatarNote>
        <InstructorRow>
          <PhotoWrap>
            <Photo
              src="/assets/images/instructor-park-jiwon.png"
              alt="Rechard Park (Zoom용 AI 생성 아바타)"
            />
            <PhotoCaption>* Zoom 미팅용 AI 생성 아바타 이미지</PhotoCaption>
          </PhotoWrap>
          <Info>
            <Name>Rechard Park</Name>
            <Role>시니어 풀스택 / 백엔드 멘토</Role>
            <Bio>
              대규모 트래픽을 다루는 스트리밍 플랫폼과 사내 시스템 백엔드를 두루 거친 시니어 개발자입니다.
              Java/Spring 기반의 견고한 백엔드 설계, 자료구조와 알고리즘의 실전 활용, 그리고 최근에는 AI
              도구를 결합한 개발 생산성 최적화까지 폭넓게 다룹니다. 여러 스타트업의 기술 고문으로도 활동
              중이며, 현장에서 통하는 코드와 팀 내 협업 방식을 동시에 가르치는 데 강점이 있습니다.
            </Bio>
            <Specialties>
              <Chip>Java</Chip>
              <Chip>Spring</Chip>
              <Chip>분산 시스템</Chip>
              <Chip>DB 모델링</Chip>
              <Chip>AI Coding</Chip>
              <Chip>Code Review</Chip>
            </Specialties>
            <Courses>
              <strong>참여 코스</strong>
              Java 기초 / Java 심화 / C 프로그래밍 / 데이터베이스 모델링 / React / AI 코딩
            </Courses>
          </Info>
        </InstructorRow>

        <InstructorRow>
          <PhotoWrap>
            <Photo
              src="/assets/images/instructor-choi-yujin.png"
              alt="Lisa Choi (Zoom용 AI 생성 아바타)"
            />
            <PhotoCaption>* Zoom 미팅용 AI 생성 아바타 이미지</PhotoCaption>
          </PhotoWrap>
          <Info>
            <Name>Lisa Choi</Name>
            <Role>프론트엔드 아키텍트</Role>
            <Bio>
              대형 숙박 플랫폼의 프론트엔드 아키텍처를 설계·운영한 경험이 있는 시니어 프론트엔드 개발자입니다.
              React/TypeScript 기반의 대규모 코드베이스 운영과 성능 최적화, 마이크로 프론트엔드, 그리고
              디자인 시스템 구축까지 다뤘습니다. 학습자가 실제로 손에 쥐고 쓸 수 있는 컴포넌트 단위의 사고법과
              타입 시스템을 통한 안정성 확보를 강조합니다.
            </Bio>
            <Specialties>
              <Chip>React</Chip>
              <Chip>TypeScript</Chip>
              <Chip>Design System</Chip>
              <Chip>Frontend Architecture</Chip>
              <Chip>Performance</Chip>
              <Chip>AI Coding</Chip>
            </Specialties>
            <Courses>
              <strong>참여 코스</strong>
              React / TypeScript / AI 코딩
            </Courses>
          </Info>
        </InstructorRow>
      </Section>
    </Page>
  );
};

export default InstructorsPage;
